// MaturaTrigTanRelationClosedTask.js
// Zadanie maturalne zamknięte: z relacji z tangensem wyznaczamy cosinus.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const VALUES = {
  half: { key: "half", latex: "\\frac{1}{2}" },
  rt2over2: { key: "rt2over2", latex: "\\frac{\\sqrt{2}}{2}" },
  rt3over2: { key: "rt3over2", latex: "\\frac{\\sqrt{3}}{2}" },
  rt3over3: { key: "rt3over3", latex: "\\frac{\\sqrt{3}}{3}" },
  one: { key: "one", latex: "1" },
  rt3: { key: "rt3", latex: "\\sqrt{3}" },
};

const VARIANTS = [
  {
    mode: "cosFromTanSin",
    relation: "\\sqrt{3}\\,\\operatorname{tg}\\alpha=2\\sin\\alpha",
    leftCoef: "\\sqrt{3}",
    rightCoef: "2",
    target: "cos",
    correct: VALUES.rt3over2,
  },
  {
    mode: "cosFromTanSin",
    relation: "\\sqrt{2}\\,\\operatorname{tg}\\alpha=2\\sin\\alpha",
    leftCoef: "\\sqrt{2}",
    rightCoef: "2",
    target: "cos",
    correct: VALUES.rt2over2,
  },
  {
    mode: "cosFromTanSin",
    relation: "\\operatorname{tg}\\alpha=2\\sin\\alpha",
    leftCoef: "1",
    rightCoef: "2",
    target: "cos",
    correct: VALUES.half,
  },
];

function buildOptions(correct) {
  const pool = [
    correct,
    VALUES.half,
    VALUES.rt2over2,
    VALUES.rt3over2,
    VALUES.rt3over3,
    VALUES.one,
    VALUES.rt3,
  ];

  const unique = [];
  const seen = new Set();
  pool.forEach((item) => {
    if (!seen.has(item.key)) {
      seen.add(item.key);
      unique.push(item);
    }
  });

  const optionValues = [correct, ...unique.filter((item) => item.key !== correct.key).slice(0, 3)];
  return shuffle(optionValues).map((item, idx) => ({
    ...item,
    label: LETTERS[idx],
    isCorrect: item.key === correct.key,
  }));
}

function generateMay2025Original() {
  const variant = VARIANTS[0];
  const optionValues = [
    VALUES.half,
    VALUES.rt2over2,
    VALUES.rt3over2,
    VALUES.rt3over3,
  ];
  const options = optionValues.map((item, idx) => ({
    ...item,
    label: LETTERS[idx],
    isCorrect: item.key === variant.correct.key,
  }));

  return {
    type: "MaturaTrigTanRelationClosedTask",
    data: {
      ...variant,
      options,
      correctIndex: 2,
      maxPoints: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 17,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  const variant = pick(VARIANTS);
  const options = buildOptions(variant.correct);

  return {
    type: "MaturaTrigTanRelationClosedTask",
    data: {
      ...variant,
      options,
      correctIndex: options.findIndex((opt) => opt.isCorrect),
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { relation, target, options } = data;
  const targetLabel = target === "cos" ? "Cosinus" : "Sinus";

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Kąt <InlineMath math={"\\alpha"} /> jest ostry i spełnia warunek{" "}
        <InlineMath math={relation} />.
      </p>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>

      <p>
        {targetLabel} kąta <InlineMath math={"\\alpha"} /> jest równy
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.key,
    });
  };

  return (
    <ABCDInput
      options={options.map((opt, idx) => ({
        value: idx,
        label: opt.label,
        textNode: opt.label,
      }))}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderExplanation({ data }) {
  const {
    relation,
    leftCoef,
    rightCoef,
    correct,
  } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Podstawiamy definicję tangensa bezpośrednio
        do równania z treści:
      </p>
      <BlockMath
        math={`\\begin{aligned}
          ${relation}\\\\
          ${leftCoef}\\cdot\\frac{\\sin\\alpha}{\\cos\\alpha}
          &=${rightCoef}\\sin\\alpha
        \\end{aligned}`}
      />

      <p>
        <strong>Krok 2.</strong> Kąt <InlineMath math={"\\alpha"} /> jest ostry,
        więc <InlineMath math={"\\sin\\alpha>0"} />. Dzielimy obie strony
        przez <InlineMath math={"\\sin\\alpha"} /> i skracamy:
      </p>
      <BlockMath
        math={`\\frac{${leftCoef}}{\\cos\\alpha}=${rightCoef}`}
      />

      <p>
        <strong>Krok 3.</strong> Przekształcamy równanie i otrzymujemy szukaną
        wartość:
      </p>
      <BlockMath
        math={`\\cos\\alpha=${correct.latex}`}
      />
    </div>
  );
}
export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}
